import React, { useState, useEffect } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress, Popper, Fade } from '@mui/material';
import { WalteroCard, WalteroTypography } from '../waltero';
import { useSharedPerformanceCache } from '../../context/SharedPerformanceCache';

/**
 * Simple Boot Reason Table - Device restart information
 * 
 * Shows device restart activity with details about boot reasons.
 * Displays count of problematic reboots per day, with hover details showing
 * full breakdown of all boot reason types.
 */
const SimpleBootReasonTable = ({ selectedOrgId, startDate, endDate }) => {
  const [bootReasonData, setBootReasonData] = useState({});
  
  const { 
    loadAllPerformanceData,
    getEndpointData,
    getDevices,
    cacheInfo,
    isEndpointLoading
  } = useSharedPerformanceCache();
  
  // Hover overlay state
  const [hoverData, setHoverData] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    loadBootReasonData();
  }, [selectedOrgId, startDate, endDate]);

  // Update data whenever cache changes
  useEffect(() => {
    const devices = getDevices();
    const dataMap = {};
    
    devices.forEach(device => {
      const data = getEndpointData('boot-reason', device.id);
      if (data) {
        dataMap[device.id] = data;
      }
    });
    
    setBootReasonData(dataMap);
  }, [cacheInfo.endpointsLoaded, getEndpointData, getDevices]);

  const loadBootReasonData = () => {
    if (!selectedOrgId || !startDate || !endDate) return;
    
    loadAllPerformanceData(selectedOrgId, startDate, endDate);
  };

  // Helper function to get non-interval boot counts for a specific device and date
  const getNonIntervalBootsForDay = (deviceId, date) => {
    const deviceData = bootReasonData[deviceId];
    if (!deviceData?.bootReasonMatrix) return { count: 0, details: {} };
    
    const dayBootReasons = deviceData.bootReasonMatrix.filter(entry => entry.date === date);
    let nonIntervalCount = 0;
    const details = {};
    
    dayBootReasons.forEach(entry => {
      details[entry.bootReason] = entry.count;
      if (entry.bootReason.toLowerCase() !== 'interval') {
        nonIntervalCount += entry.count;
      }
    });
    
    return { count: nonIntervalCount, details };
  };

  // Handle hover
  const handleCellHover = (event, device, date) => {
    const bootInfo = getNonIntervalBootsForDay(device.id, date);
    
    if (Object.keys(bootInfo.details).length > 0) {
      setHoverData({
        device,
        date,
        bootInfo
      });
      setAnchorEl(event.currentTarget);
      setOpen(true);
    }
  };

  const handleCellLeave = () => {
    setOpen(false);
    setHoverData(null);
    setAnchorEl(null);
  };

  // Get devices from shared cache
  const devices = getDevices();

  // Generate all dates in the selected range (regardless of data availability)
  const generateDateRange = (start, end) => {
    const dates = [];
    const currentDate = new Date(start);
    const endDate = new Date(end);
    
    while (currentDate <= endDate) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };

  // Generate dates in reverse chronological order (most recent first)
  const sortedDates = startDate && endDate ? generateDateRange(startDate, endDate).sort((a, b) => b.localeCompare(a)) : [];


  return (
    <WalteroCard sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <WalteroTypography variant="h6">
          Device Boot Reasons - Non-Interval Boots
        </WalteroTypography>
        {cacheInfo.isLoading && (
          <CircularProgress size={16} />
        )}
      </Box>
      
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
Shows count of problematic device reboots per day. Hover over cells to see detailed breakdown of all boot reason types.
      </Typography>
      

      {devices.length === 0 ? (
        <Typography>No devices found for this organization.</Typography>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fce4ec' }}>
                    Device
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fce4ec' }}>
                    Meter
                  </TableCell>
                  {sortedDates.map(date => (
                    <TableCell 
                      key={date} 
                      align="center" 
                      sx={{ 
                        fontWeight: 'bold', 
                        backgroundColor: '#fce4ec',
                        minWidth: '80px',
                        fontSize: '12px'
                      }}
                    >
                      {date.split('-').slice(1).join('/')}
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fce4ec' }}>
                    Total Issues
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.sort((a, b) => {
                  const serialA = String(a.serialnumber || '');
                  const serialB = String(b.serialnumber || '');
                  return serialA.localeCompare(serialB);
                }).map(device => {
                  const deviceBootData = bootReasonData[device.id];
                  const totalIssues = deviceBootData?.summary?.problematicBoots || 0;
                  
                  return (
                    <TableRow key={device.id}>
                      <TableCell sx={{ fontWeight: '600', fontSize: '12px' }}>
                        {device.serialnumber}
                      </TableCell>
                      <TableCell sx={{ fontSize: '10px', color: '#666' }}>
                        {device.externalmeterid || 'N/A'}
                      </TableCell>
                      {sortedDates.map(date => {
                        const bootInfo = getNonIntervalBootsForDay(device.id, date);
                        const nonIntervalCount = bootInfo.count;
                        
                        return (
                          <TableCell 
                            key={date} 
                            align="center"
                            sx={{
                              backgroundColor: nonIntervalCount > 0 ? '#ffebee' : 
                                             Object.keys(bootInfo.details).length > 0 ? '#e8f5e8' : '#fafafa',
                              fontSize: '11px',
                              fontWeight: '600',
                              color: nonIntervalCount > 0 ? '#d32f2f' : '#4caf50',
                              cursor: Object.keys(bootInfo.details).length > 0 ? 'pointer' : 'default'
                            }}
                            onMouseEnter={(e) => handleCellHover(e, device, date)}
                            onMouseLeave={handleCellLeave}
                          >
                            {isEndpointLoading('boot-reason', device.id) ? (
                              <CircularProgress size={12} />
                            ) : nonIntervalCount > 0 ? (
                              nonIntervalCount
                            ) : Object.keys(bootInfo.details).length > 0 ? (
                              '✓'
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell 
                        align="center" 
                        sx={{ 
                          fontWeight: '600', 
                          backgroundColor: '#fce4ec',
                          color: totalIssues > 0 ? '#d32f2f' : '#4caf50'
                        }}
                      >
                        {totalIssues}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Hover Overlay */}
          <Popper 
            open={open} 
            anchorEl={anchorEl} 
            placement="top" 
            transition
            style={{ zIndex: 1000 }}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={200}>
                <Paper 
                  sx={{ 
                    p: 2, 
                    maxWidth: 300, 
                    backgroundColor: '#333', 
                    color: 'white',
                    fontSize: '12px'
                  }}
                >
                  {hoverData && (
                    <div>
                      <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 1, color: 'white' }}>
                        {hoverData.device.serialnumber} - {hoverData.date}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, color: '#ccc' }}>
                        Boot Reason Breakdown:
                      </Typography>
                      {Object.entries(hoverData.bootInfo.details).map(([reason, count]) => (
                        <div key={reason} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ 
                            color: reason.toLowerCase() === 'interval' ? '#4caf50' : '#ff9800' 
                          }}>
                            {reason}:
                          </span>
                          <span style={{ fontWeight: '600' }}>{count}</span>
                        </div>
                      ))}
                      <hr style={{ margin: '8px 0', borderColor: '#555' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '600' }}>
                        <span>Non-Interval Boots:</span>
                        <span style={{ color: hoverData.bootInfo.count > 0 ? '#ff5722' : '#4caf50' }}>
                          {hoverData.bootInfo.count}
                        </span>
                      </div>
                    </div>
                  )}
                </Paper>
              </Fade>
            )}
          </Popper>
        </>
      )}

      {/* Summary */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: '#fce4ec', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: '600' }}>
          Summary: {devices.length} devices, {Object.keys(bootReasonData).length} with boot data, {sortedDates.length} days
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Colors: Red = Problematic boots, Green = Only interval boots, Gray = No data
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Hover cells to see detailed boot reason breakdown
        </Typography>
      </Box>
    </WalteroCard>
  );
};

export default SimpleBootReasonTable;